import { Modal, Stack, Group, Text, Badge, Button, ScrollArea, Card } from '@mantine/core';
import { useAnnouncements, useMarkAsRead, useMarkAllAsRead } from '../api/hooks';
import { ANNOUNCEMENT_TYPE_CONFIG } from '../types';
import type { Announcement } from '../types';

interface AnnouncementModalProps {
  opened: boolean;
  onClose: () => void;
}

function AnnouncementItem({
  item,
  onMarkRead,
}: {
  item: Announcement;
  onMarkRead: (id: string) => void;
}) {
  const config = ANNOUNCEMENT_TYPE_CONFIG[item.type] ?? { label: item.type, color: 'gray' };

  return (
    <Card
      padding="sm"
      radius="sm"
      withBorder
      style={{
        opacity: item.is_read ? 0.6 : 1,
        borderLeftWidth: 3,
        borderLeftColor: `var(--mantine-color-${config.color}-5)`,
      }}
    >
      <Group justify="space-between" mb={4}>
        <Group gap="xs">
          <Badge color={config.color} variant="light" size="sm">
            {config.label}
          </Badge>
          <Text size="sm" fw={item.is_read ? 400 : 700}>
            {item.title}
          </Text>
        </Group>
        <Group gap="xs">
          <Text size="xs" c="dimmed">
            {item.date}
          </Text>
          {!item.is_read && (
            <Button
              variant="subtle"
              size="compact-xs"
              color="gray"
              onClick={(e) => {
                e.stopPropagation();
                onMarkRead(item.id);
              }}
            >
              既読
            </Button>
          )}
        </Group>
      </Group>
      <Text size="sm" c="dimmed" style={{ whiteSpace: 'pre-line' }}>
        {item.body}
      </Text>
    </Card>
  );
}

export function AnnouncementModal({ opened, onClose }: AnnouncementModalProps) {
  const { data } = useAnnouncements();
  const markAsReadMutation = useMarkAsRead();
  const markAllAsReadMutation = useMarkAllAsRead();

  const announcements = data?.data ?? [];
  const unreadCount = data?.unread_count ?? 0;

  const handleMarkRead = (id: string) => {
    markAsReadMutation.mutate([id]);
  };

  const handleMarkAllRead = () => {
    markAllAsReadMutation.mutate();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="sm">
          <Text fw={700}>システムお知らせ</Text>
          {unreadCount > 0 && (
            <Badge color="red" variant="filled" size="sm">
              {unreadCount}件未読
            </Badge>
          )}
        </Group>
      }
      size="lg"
      overlayProps={{ opacity: 0.5, blur: 16 }}
    >
      <Stack gap="xs">
        {unreadCount > 0 && (
          <Group justify="flex-end">
            <Button
              variant="subtle"
              size="compact-sm"
              onClick={handleMarkAllRead}
              loading={markAllAsReadMutation.isPending}
            >
              すべて既読にする
            </Button>
          </Group>
        )}
        <ScrollArea.Autosize mah={400}>
          <Stack gap="xs">
            {announcements.length === 0 ? (
              <Text c="dimmed" ta="center" py="md">
                お知らせはありません
              </Text>
            ) : (
              announcements.map((item) => (
                <AnnouncementItem key={item.id} item={item} onMarkRead={handleMarkRead} />
              ))
            )}
          </Stack>
        </ScrollArea.Autosize>
      </Stack>
    </Modal>
  );
}
